import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, CheckCircle2, PlusCircle, ArrowRight } from "lucide-react";
import { ADDONS } from "@/lib/api";

const EYEBROW = "text-xs uppercase tracking-[0.22em] font-bold text-[#5BA83F]";
const H2 = "text-3xl sm:text-4xl text-[#0F172A] leading-tight mt-2";
const SERVICE_KEYS = ["residential", "deep", "move", "office", "post_reno", "window", "garden", "airbnb"];

export function CityServices({ t, area }) {
  return (
    <section className="py-12 md:py-16 bg-[#FAF9F6]" data-testid="nl-city-services">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className={EYEBROW}>{area.city}</p>
            <h2 className={H2}>{t("nl_city_page.services_title", { city: area.city })}</h2>
            <p className="mt-2 text-sm text-slate-600">{t("nl_city_page.services_sub", { city: area.city })}</p>
          </div>
          <Link to="/services" className="text-sm font-semibold text-[#1B3A6B] hover:text-[#5BA83F]" data-testid="nl-city-services-link">{t("nl_city_page.services_link")} →</Link>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SERVICE_KEYS.map((k) => (
            <Link key={k} to={`/book?city=${area.slug}`} data-testid={`nl-city-service-${k}`}
              className="group bg-white rounded-2xl p-5 ring-1 ring-[#E5E0D8] hover:ring-[#1B3A6B]/30 transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md">
              <Sparkles className="w-5 h-5 text-[#5BA83F]" />
              <h3 className="mt-3 font-bold text-[#0F172A] text-sm">{t(`services.${k}`)}</h3>
              <p className="mt-1 text-xs text-slate-600 leading-relaxed">{t(`services.${k}_desc`)}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#5BA83F]">{t("nl_city_page.book_service")} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CityInclusions({ t }) {
  const rooms = t("checklist_page_v2.categories.regular.rooms", { returnObjects: true }) || [];
  return (
    <section className="py-12 md:py-16" data-testid="nl-city-inclusions">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 rounded-2xl ring-1 ring-slate-200 p-6">
          <h2 className="text-2xl sm:text-3xl text-[#0F172A]">{t("nl_city_page.included_title")}</h2>
          <p className="mt-1 text-sm text-slate-500">{t("nl_city_page.included_sub")}</p>
          <div className="mt-5 grid sm:grid-cols-3 gap-5">
            {(Array.isArray(rooms) ? rooms : []).slice(0, 3).map((r) => (
              <div key={r.room}>
                <h3 className="font-bold text-sm text-[#1B3A6B]">{r.room}</h3>
                <ul className="mt-2 space-y-1.5">
                  {r.items.slice(0, 4).map((i) => (
                    <li key={i} className="flex gap-2 text-xs text-slate-600"><CheckCircle2 className="w-3.5 h-3.5 text-[#5BA83F] shrink-0 mt-0.5" />{i}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Link to="/checklist" className="mt-5 inline-block text-sm font-semibold text-[#1B3A6B] hover:text-[#5BA83F]" data-testid="nl-city-checklist-link">{t("nl_city_page.checklist_link")} →</Link>
        </div>
        <div className="lg:col-span-4 rounded-2xl bg-[#F8FAFC] ring-1 ring-slate-200 p-6" data-testid="nl-city-exclusions">
          <h2 className="text-2xl text-[#0F172A]">{t("nl_city_page.excluded_title")}</h2>
          <p className="mt-1 text-xs text-slate-500">{t("nl_city_page.excluded_sub")}</p>
          <ul className="mt-3 space-y-1.5">
            {ADDONS.map((a) => (
              <li key={a.key} className="flex gap-2 text-xs text-slate-700"><PlusCircle className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />{t(`book_v2.extra_labels.${a.key}`, a.label)}</li>
            ))}
          </ul>
          <p className="mt-4 text-[11px] italic text-slate-500 leading-relaxed">{t("nl_city_page.excluded_note")}</p>
        </div>
      </div>
    </section>
  );
}

export function CityHowItWorks({ t }) {
  return (
    <section className="py-12 md:py-16 bg-[#F8FAFC]" data-testid="nl-city-how">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <h2 className="text-3xl sm:text-4xl text-[#0F172A]">{t("nl_city_page.how_title")}</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="rounded-2xl p-5 bg-white ring-1 ring-slate-200">
              <div className="font-display text-4xl text-[#5BA83F]/30">0{n}</div>
              <h3 className="mt-2 font-bold text-[#0F172A]">{t(`home.how_step${n}_title`)}</h3>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">{t(`home.how_step${n}_desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
